import type { UseFormReturn } from 'react-hook-form';
import type { Store, BookingFormData, ModelWithVersions } from '@geely/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Form,
  FormField,
  FormItem,
  FormControl,
  FormLabel,
  FormMessage,
} from '@geely/ui';
import { ArrowLeft, MapPin, Calendar, Clock, User, Phone, Mail, IdCard, Car, Gauge, RefreshCw } from 'lucide-react';
import { formatDate, formatTime } from '@/lib/utils';

interface ReviewStepProps {
  selectedStore: Store;
  selectedDate: string;
  selectedTime: string;
  form: UseFormReturn<BookingFormData>;
  models: ModelWithVersions[];
  isSubmitting: boolean;
  isRescheduling: boolean;
  onBack: () => void;
  onSubmit: () => void;
}

export function ReviewStep({
  selectedStore,
  selectedDate,
  selectedTime,
  form,
  models,
  isSubmitting,
  isRescheduling,
  onBack,
  onSubmit,
}: ReviewStepProps) {
  const values = form.getValues();
  const lgpdConsent = form.watch('lgpdConsent');
  const selectedModel = models.find((m) => m.id === values.modelId);
  const selectedVersion = selectedModel?.versions.find((v) => v.id === values.versionId);

  const formatMileage = (km: number) => {
    return km?.toLocaleString('pt-BR') + ' km';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{'Revis\u00e3o do Agendamento'}</CardTitle>
        <CardDescription>{'Confirme as informa\u00e7\u00f5es antes de finalizar'}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {/* Store info */}
          <div className="flex items-start gap-3">
            <MapPin className="size-5 text-[#000000] mt-0.5" />
            <div>
              <p className="font-medium text-[#080404]">Loja</p>
              <p className="text-sm text-[#64748b]">{selectedStore.name}</p>
              <p className="text-sm text-[#64748b]">
                {selectedStore.street}, {selectedStore.number} - {selectedStore.city}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Calendar className="size-5 text-[#000000] mt-0.5" />
            <div>
              <p className="font-medium text-[#080404]">Data</p>
              <p className="text-sm text-[#64748b]">{formatDate(selectedDate)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="size-5 text-[#000000] mt-0.5" />
            <div>
              <p className="font-medium text-[#080404]">{'Hor\u00e1rio'}</p>
              <p className="text-sm text-[#64748b]">{formatTime(selectedTime)}</p>
            </div>
          </div>

          {/* Customer data */}
          <div className="border-t border-[#e2e8f0] pt-4 mt-4">
            <h4 className="font-medium mb-3 text-[#080404]">Dados do Cliente</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <User className="size-5 text-[#000000] mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-[#080404]">Nome</p>
                  <p className="text-sm text-[#64748b]">{values.customerName}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="size-5 text-[#000000] mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-[#080404]">Telefone</p>
                  <p className="text-sm text-[#64748b]">{values.customerPhone}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="size-5 text-[#000000] mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-[#080404]">Email</p>
                  <p className="text-sm text-[#64748b]">{values.customerEmail}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <IdCard className="size-5 text-[#000000] mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-[#080404]">CPF / CNPJ</p>
                  <p className="text-sm text-[#64748b]">{values.customerCpf}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle data */}
          <div className="border-t border-[#e2e8f0] pt-4 mt-4">
            <h4 className="font-medium mb-3 text-[#080404]">{'Dados do Ve\u00edculo'}</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Car className="size-5 text-[#000000] mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-[#080404]">{'Modelo / Vers\u00e3o'}</p>
                  <p className="text-sm text-[#64748b]">
                    {selectedModel?.name || '-'} - {selectedVersion?.name || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Car className="size-5 text-[#000000] mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-[#080404]">Placa</p>
                  <p className="text-sm text-[#64748b]">{values.vehiclePlate}</p>
                </div>
              </div>
              {values.vehicleVin && (
                <div className="flex items-start gap-3">
                  <Car className="size-5 text-[#000000] mt-0.5" />
                  <div>
                    <p className="font-medium text-sm text-[#080404]">Chassi (VIN)</p>
                    <p className="text-sm text-[#64748b]">{values.vehicleVin}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Gauge className="size-5 text-[#000000] mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-[#080404]">Quilometragem</p>
                  <p className="text-sm text-[#64748b]">{formatMileage(values.vehicleMileage)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* LGPD Consent - only for new bookings */}
        {!isRescheduling && (
          <div className="border-t border-[#e2e8f0] pt-4">
            <Form {...form}>
              <FormField
                control={form.control}
                name="lgpdConsent"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-2 leading-normal">
                      <FormLabel className="text-sm font-normal text-[#64748b] cursor-pointer">
                        {'A marca Geely, representada por meio de sua licenciada, Renault do Brasil, considera a prote\u00e7\u00e3o de seus dados uma prioridade. Assim, a Geely esclarece que os dados coletados s\u00e3o tratados nos termos da Lei Geral de Prote\u00e7\u00e3o de Dados (Lei n\u00ba 13.709/18 \u2013 LGPD).'}
                      </FormLabel>
                      <p className="text-sm text-[#64748b]">
                        {'Os dados s\u00e3o utilizados para a realiza\u00e7\u00e3o de agendamento de servi\u00e7os de revis\u00e3o, sendo compartilhados com a concession\u00e1ria selecionada pelo cliente, exclusivamente para os fins de atendimento de suas solicita\u00e7\u00f5es e/ou necessidades.'}
                      </p>
                      <p className="text-sm text-[#64748b]">
                        {'Voc\u00ea tem o direito de acessar, solicitar a retifica\u00e7\u00e3o ou a exclus\u00e3o de seus dados pessoais. Voc\u00ea tamb\u00e9m tem direito de retirar, a qualquer momento, o seu consentimento, para os fins aos quais o mesmo foi obtido, dentre outros direitos previstos na LGPD. Para exerc\u00ea-los, entre em contato atrav\u00e9s do e-mail dpo@geelybrasil.com.br.'}
                      </p>
                      <p className="text-sm text-[#64748b]">
                        {'Com o preenchimento do presente formul\u00e1rio, voc\u00ea atesta a sua maioridade e autoriza a Geely a coletar e tratar suas informa\u00e7\u00f5es com a rede de concession\u00e1rios autorizados, empresas do Grupo Renault e outras empresas parceiras, exclusivamente para os fins descritos na Pol\u00edtica de Privacidade da Geely Brasil.'}
                      </p>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </Form>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4">
          <Button variant="outline" onClick={onBack} disabled={isSubmitting} className="w-full sm:w-auto">
            <ArrowLeft className="size-4 mr-2" />
            Voltar
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isSubmitting || (!isRescheduling && !lgpdConsent)}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? (
              <>
                <RefreshCw className="size-4 mr-2 animate-spin" />
                Confirmando...
              </>
            ) : (
              isRescheduling ? 'Confirmar Remarca\u00e7\u00e3o' : 'Confirmar Agendamento'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
